import { Injectable } from '@angular/core';

export interface ConversationEntry {
  companyUUID: string;
  userId: string;
  type: 'job' | 'event';
  timestamp: number;
  messages: { role: 'user' | 'ai', content: string }[];
}

@Injectable({ providedIn: 'root' })
export class ConversationHistoryService {
  private dbName = 'CopilotConversationsDB';
  private storeName = 'conversations';
  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = this.openDB();
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async saveConversation(entry: ConversationEntry): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      store.add({ ...entry, id: this.getEntryId(entry) });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async appendToConversation(entry: ConversationEntry): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const id = this.getEntryId(entry);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const existing = getReq.result;
        if (existing) {
          existing.messages = [...existing.messages, ...entry.messages];
          existing.timestamp = entry.timestamp;
          store.put(existing);
        } else {
          store.add({ ...entry, id });
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getConversation(companyUUID: string, userId: string, type: 'job' | 'event'): Promise<ConversationEntry | null> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const id = this.getEntryId({ companyUUID, userId, type });
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  async getLatestConversation(companyUUID: string, userId: string, type: 'job' | 'event'): Promise<ConversationEntry | null> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const entries: ConversationEntry[] = [];
      const req = store.openCursor();
      req.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          const entry = cursor.value as ConversationEntry;
          if (entry.companyUUID === companyUUID && entry.userId === userId && entry.type === type) {
            entries.push(entry);
          }
          cursor.continue();
        } else {
          if (entries.length === 0) return resolve(null);
          entries.sort((a, b) => b.timestamp - a.timestamp);
          resolve(entries[0]);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  async clearConversations(companyUUID: string, userId: string, type?: 'job' | 'event'): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const req = store.openCursor();
      req.onsuccess = (event: any) => {
        const cursor = event.target.result;
        if (cursor) {
          const entry = cursor.value as ConversationEntry;
          if (entry.companyUUID === companyUUID && entry.userId === userId && (!type || entry.type === type)) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  private getEntryId(entry: { companyUUID: string; userId: string; type: 'job' | 'event'; }): string {
    return `${entry.companyUUID}_${entry.userId}_${entry.type}`;
  }
} 